import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { canAccessPath } from '../accessControl';
import { businessProfileFromUser } from '../businessProfiles';
import { peekErpSessionActor } from '../services/erpSessionClient';
import './Sidebar.css';

const BASE_GROUPS = [
  {
    title: 'Vendas',
    icon: 'fa-shopping-bag',
    items: [
      { to: '/', icon: 'fa-chart-line', label: 'Dashboard' },
      { to: '/pdv', icon: 'fa-shopping-cart', label: 'PDV' },
      { to: '/caixa', icon: 'fa-cash-register', label: 'Caixa' },
      { to: '/comandas-mobile', icon: 'fa-clipboard-list', label: 'Comandas (neste caixa)' },
      { to: '/entregas', icon: 'fa-motorcycle', label: 'Gestão de Entregas' },
      { to: '/pedidos', icon: 'fa-clipboard-list', label: 'Pedidos' },
      { to: '/mesas', icon: 'fa-chair', label: 'Mesas' }
    ]
  },
  {
    title: 'Cadastros',
    icon: 'fa-boxes',
    items: [
      { to: '/clientes', icon: 'fa-users', label: 'Clientes' },
      { to: '/fornecedores', icon: 'fa-truck', label: 'Fornecedores' },
      { to: '/entregadores', icon: 'fa-id-badge', label: 'Entregadores' },
      { to: '/tabela-frete', icon: 'fa-route', label: 'Tabela de Frete' },
      { to: '/produtos', icon: 'fa-box', label: 'Produtos' },
      { to: '/tabelas-preco', icon: 'fa-tags', label: 'Tabelas de preço' },
      { to: '/inventario', icon: 'fa-clipboard-check', label: 'Inventário e ajustes' },
      { to: '/catalogo-operacional', icon: 'fa-layer-group', label: 'Grades e produção' },
      { to: '/categorias-produtos', icon: 'fa-tags', label: 'Categorias' }
    ]
  },
  {
    title: 'Pessoas',
    icon: 'fa-user-friends',
    items: [
      { to: '/colaboradores', icon: 'fa-user-tie', label: 'Colaboradores' },
      { to: '/ponto', icon: 'fa-fingerprint', label: 'Ponto' },
      { to: '/agenda-comissoes', icon: 'fa-calendar-check', label: 'Agenda e comissões' },
      { to: '/base-conhecimento', icon: 'fa-book', label: 'Base de Conhecimento' }
    ]
  },
  {
    title: 'Relatórios',
    icon: 'fa-chart-pie',
    items: [
      { to: '/relatorios', icon: 'fa-chart-bar', label: 'Relatórios' },
      { to: '/inteligencia-publico', icon: 'fa-brain', label: 'Inteligência de Público' },
      { to: '/consultas', icon: 'fa-database', label: 'Consultas' }
    ]
  },
  {
    title: 'Financeiro',
    icon: 'fa-wallet',
    items: [
      { to: '/financeiro', icon: 'fa-wallet', label: 'Fluxo de Caixa' },
      { to: '/financeiro-organizador', icon: 'fa-folder-open', label: 'Organizador' },
      { to: '/dre', icon: 'fa-chart-line', label: 'DRE' },
      { to: '/crediario', icon: 'fa-ticket-alt', label: 'Crediário' },
      { to: '/financeiro-configuracoes', icon: 'fa-sliders-h', label: 'Parametrização Financeira' },
      { to: '/ordens-compra', icon: 'fa-file-invoice', label: 'Ordens de Compra' }
    ]
  },
  {
    title: 'Contabilidade',
    icon: 'fa-calculator',
    items: [
      { to: '/espaco-contador', icon: 'fa-file-export', label: 'Espaço do Contador' }
    ]
  },
  {
    title: 'Integrações',
    icon: 'fa-plug',
    items: [
      { to: '/fiscal', icon: 'fa-file-invoice-dollar', label: 'Fiscal / NFE.io' },
      { to: '/dispositivos', icon: 'fa-print', label: 'Dispositivos' },
      { to: '/conecta-clube', icon: 'fa-sync-alt', label: 'Conecta Clube (fidelidade)' },
      { to: '/wbuy', icon: 'fa-globe', label: 'WBUY Ecommerce' },
      { to: '/correios', icon: 'fa-shipping-fast', label: 'Correios PAC / SEDEX' },
      { to: '/pagarme', icon: 'fa-credit-card', label: 'Pagar.me' },
    ]
  },
  {
    title: 'Conta e suporte',
    icon: 'fa-sliders-h',
    items: [
      { to: '/configuracoes', icon: 'fa-cogs', label: 'Configurações' },
      { to: '/faq-suporte', icon: 'fa-life-ring', label: 'FAQ e Suporte' },
      { to: '/banners-parceiros', icon: 'fa-images', label: 'Banners Parceiros' },
      { to: '/instalador-local', icon: 'fa-download', label: 'Instalador Local' }
    ]
  }
];

const QUICK_ACCESS_PATHS = ['/', '/pdv', '/caixa', '/comandas-mobile', '/pedidos', '/entregas', '/espaco-contador', '/faq-suporte'];
const QUICK_ACCESS_LABELS = {
  '/comandas-mobile': 'Comandas',
  '/faq-suporte': 'Suporte'
};

function safeId(value) {
  return String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function labelsForBusinessProfile(profile) {
  if (!profile.weightedSales) return {};
  return {
    '/pdv': 'PDV por unidade ou peso',
    '/catalogo-operacional': profile.code === 'food_service'
      ? 'Catálogo, peso e produção'
      : 'Catálogo e venda por peso',
    '/dispositivos': 'Balança e dispositivos'
  };
}

export function sidebarGroupsForUser(user, actor = null) {
  const authoritativeTenant = actor?.tenant;
  const accessUser = authoritativeTenant?.modules
    ? {
        ...user,
        business_profile: authoritativeTenant.businessProfile,
        tenant_modules: authoritativeTenant.modules,
        tenant_modules_version: authoritativeTenant.modulesVersion,
        tenant: { ...(user?.tenant || {}), ...authoritativeTenant }
      }
    : user;
  const businessProfile = businessProfileFromUser(accessUser, actor);
  const profileLabels = labelsForBusinessProfile(businessProfile);
  const groups = BASE_GROUPS.map((group) => ({
    ...group,
    items: group.items
      .filter((item) => canAccessPath(accessUser, item.to))
      .map((item) => profileLabels[item.to] ? { ...item, label: profileLabels[item.to] } : item)
  })).filter((group) => group.items.length > 0);
  return { businessProfile, groups };
}

export default function Sidebar({ user, open = false, onNavigate }) {
  const location = useLocation();
  const nivoraMark = `${process.env.PUBLIC_URL || ''}/assets/nivora-favicon.png`;
  const activeMenuPath = location.pathname;
  const { businessProfile, groups } = sidebarGroupsForUser(user, peekErpSessionActor());

  // O owner global e uma identidade separada e usa a rota HTTP /owner.
  // Nunca exponha esse control plane dentro do menu operacional do tenant.
  const activeGroupTitle = groups.find((group) => (
    group.items.some((item) => item.to === activeMenuPath)
  ))?.title || '';
  const [openGroup, setOpenGroup] = useState(activeGroupTitle);

  useEffect(() => {
    if (activeGroupTitle) setOpenGroup(activeGroupTitle);
  }, [activeGroupTitle]);

  const availableItems = groups.flatMap((group) => group.items);
  const quickAccessItems = QUICK_ACCESS_PATHS
    .map((path) => availableItems.find((item) => item.to === path))
    .filter(Boolean)
    .slice(0, 4);

  const isActive = (path) => activeMenuPath === path;
  const isCurrentPage = (path) => location.pathname === path;
  const handleNavigation = () => {
    if (typeof onNavigate === 'function') onNavigate();
    window.requestAnimationFrame(() => {
      const mainContent = document.querySelector('.main-content');
      if (mainContent) mainContent.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  };

  const Item = ({ to, icon, label }) => (
    <li className="sidebar-item">
      <Link
        to={to}
        className={`sidebar-link ${isActive(to) ? 'active' : ''}`}
        aria-current={isCurrentPage(to) ? 'page' : undefined}
        onClick={handleNavigation}
      >
        <i className={`fas ${icon}`} aria-hidden="true"></i>
        <span>{label}</span>
      </Link>
    </li>
  );

  return (
    <aside className={`sidebar ${open ? 'is-open' : ''}`} aria-label="Menu principal">
      <Link
        to="/"
        className="sidebar-brand"
        aria-label="Nivora ERP — ir para a visão geral"
        onClick={handleNavigation}
      >
        <span className="sidebar-brand-mark">
          <img src={nivoraMark} alt="" aria-hidden="true" />
        </span>
        <span className="sidebar-brand-copy">
          <strong>Nivora</strong>
          <small>ERP</small>
        </span>
      </Link>

      <nav className="sidebar-navigation" aria-label="Navegação do ERP">
        <ul className="sidebar-menu">
        <li className="sidebar-business-mode" aria-label={businessProfile.modeLabel}>
            <i
              className={`fas ${businessProfile.weightedSales ? 'fa-balance-scale' : 'fa-store'}`}
              aria-hidden="true"
            ></i>
            <span>
              <strong>{businessProfile.modeLabel}</strong>
              <small>{businessProfile.description}</small>
            </span>
        </li>

        {quickAccessItems.length > 0 && (
          <li className="sidebar-quick-access">
            <div className="sidebar-quick-heading">
              <i className="fas fa-star" aria-hidden="true"></i>
              <span>Acessos rápidos</span>
            </div>
            <div className="sidebar-quick-grid" aria-label="Acessos rápidos">
              {quickAccessItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`sidebar-quick-link ${isActive(item.to) ? 'active' : ''}`}
                  aria-current={isCurrentPage(item.to) ? 'page' : undefined}
                  title={item.label}
                  onClick={handleNavigation}
                >
                  <i className={`fas ${item.icon}`} aria-hidden="true"></i>
                  <span>
                    {item.to === '/pdv' && businessProfile.weightedSales
                      ? 'PDV por peso'
                      : QUICK_ACCESS_LABELS[item.to] || item.label}
                  </span>
                </Link>
              ))}
            </div>
          </li>
        )}

        {groups.map((group) => {
          const expanded = openGroup === group.title;
          const containsActivePage = group.title === activeGroupTitle;
          const panelId = `sidebar-group-${safeId(group.title)}`;

          return (
            <li className="sidebar-group" key={group.title}>
              <button
                type="button"
                className={`sidebar-group-toggle ${containsActivePage ? 'has-active-page' : ''}`}
                aria-expanded={expanded}
                aria-controls={panelId}
                onClick={() => setOpenGroup(expanded ? '' : group.title)}
              >
                <i className={`fas ${group.icon}`} aria-hidden="true"></i>
                <span className="sidebar-group-label">{group.title}</span>
                <span className="sidebar-group-count" aria-label={`${group.items.length} opções`}>
                  {group.items.length}
                </span>
                <i
                  className={`fas fa-chevron-down sidebar-group-chevron ${expanded ? 'is-expanded' : ''}`}
                  aria-hidden="true"
                ></i>
              </button>

              <ul className="sidebar-group-items" id={panelId} hidden={!expanded}>
                {group.items.map((item) => (
                  <Item key={item.to} {...item} />
                ))}
              </ul>
            </li>
          );
        })}
        </ul>
      </nav>

      <div className="sidebar-footer">
        <span><i className="fas fa-shield-alt" aria-hidden="true"></i></span>
        <span>
          <strong>Ambiente Nivora</strong>
          <small>Gestão segura e conectada</small>
        </span>
      </div>
    </aside>
  );
}
